import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { getFirstSentence } from "../../../helpers";
import { ITEM_TYPES, TabKeys } from "../../../types";
import { DraggableComponent } from "./components/DraggableComponent";
import { AnimationPreviewWrapper } from "./components/AnimationPreviewWrapper";
import { TextWithResizeObserver } from "./components/TextWithResizeObserver";
import { Presenter } from "./components/Presenter";
import { useCallback } from "react";
import { EditableLogo } from "./components/EditableLogo";

interface ChapterPreviewProps {
  text: string;
  previewWidth: number;
  previewHeight: number;
}
export const ChapterPreview = ({
  text,
  previewWidth,
  previewHeight,
}: ChapterPreviewProps) => {
  const { textStyle, setTextStyle, presenter } = useTemplateEditorStore(
    (store) => ({
      textStyle: store.templateState.textStyle,
      presenter: store.templateState.presenter,
      setTextStyle: store.setTextStyle,
    })
  );

  const { activeTab, setActiveTab } = useTemplatesStatusStore((state) => ({
    activeTab: state.activeTab,
    setActiveTab: state.setActiveTab,
  }));

  const sectionFragment = getFirstSentence(text);

  const handleTextResize = useCallback(
    (height: number) => {
      // Only set height if it was originally not null
      if (textStyle.size.height == null) return;

      setTextStyle({
        size: {
          width: textStyle.size.width,
          height: height / previewHeight,
        },
      });
    },
    [setTextStyle, textStyle.size.width, textStyle.size.height, previewHeight]
  );

  return (
    <>
      <EditableLogo previewWidth={previewWidth} previewHeight={previewHeight} />
      {textStyle.showSubtitles && (
        <DraggableComponent
          position={textStyle.position}
          //not used in the DraggableComponent for Text items
          size={textStyle.size}
          rotation={textStyle.rotation}
          layerIndex={textStyle.layerIndex}
          item={{
            type: ITEM_TYPES.Subtitles,
          }}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          resize={true}
          drag={true}
          onDragStop={setTextStyle}
          onRotate={(rotation) => setTextStyle({ rotation })}
          onFocus={() => {
            if (activeTab !== TabKeys.content && activeTab !== TabKeys.motion) {
              setActiveTab(TabKeys.content);
            }
          }}
        >
          <AnimationPreviewWrapper
            animations={textStyle.animations}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
            itemId={ITEM_TYPES.Subtitles}
            rotation={textStyle.rotation}
          >
            <TextWithResizeObserver
              textFragment={sectionFragment}
              textItem={textStyle}
              onResize={handleTextResize}
              currentHeight={(textStyle.size.height ?? 0) * previewHeight}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
          </AnimationPreviewWrapper>
        </DraggableComponent>
      )}
      {presenter.presenter && (
        <Presenter
          draggable={true}
          presenter={presenter}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
        />
      )}
    </>
  );
};
